import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import catchError from '../../../components/global/CatchError'
import Loader from '../../../components/global/Loader'
import Title from '../../../components/global/Title'
import { sweetAlertSuccess } from '../../../components/sweetalert/Alert'
import Table from '../../../components/table/Table'
import UserContext from '../../../context/UserContext'
import { useLangSelected } from '../../../hooks/useLangSelected'
import { useVerifyAdminToken } from '../../../hooks/useVerifyAdminToken'
import apiClient from '../../../services/apiClient'
import { BadnetTournamentModel } from '../../../types/badnetTournament.type'
import { AdminContainer, TitleWrapper } from '../users/AdminUsers'

const AdminBadnetTournaments = () => {
    const user = useContext(UserContext)
    const [tournaments, setTournaments] = useState<BadnetTournamentModel[] | undefined>(undefined)
    const [syncing, setSyncing] = useState(false)
    const [showComing, setShowComing] = useState(true)
    const [stats, setStats] = useState<any>(null)

    const verifyToken = useVerifyAdminToken()
    const lng = useLangSelected()
    const { t } = useTranslation('translation')
    const [currentLang, setCurrentLang] = useState(lng)

    const tableHeader = ['#', 'Nom', 'Titre', 'Catégories', 'Lieu', 'Date', 'Classements', 'Promu', '', '']

    useEffect(() => {
        verifyToken()
        loadTournaments()
        loadStats()
    }, [lng, showComing])

    const loadTournaments = () => {
        apiClient
            .get('/badnet/stored', {
                params: {
                    ligueId: 3306,
                    isComing: showComing,
                    lang: lng,
                },
            })
            .then(({ data }) => {
                setTournaments(
                    data.data.map((tournament: BadnetTournamentModel) => ({
                        id: tournament.id,
                        badnetId: tournament.badnetId,
                        name: tournament.name,
                        title: tournament.title,
                        categories: tournament.categories,
                        place: tournament.place,
                        date: tournament.date,
                        classifications: tournament.classifications,
                        promoted: tournament.promoted ? 'Oui' : 'Non',
                        url: tournament.url,
                        edit: `/${lng}/bva-admin/badnet/${tournament.id}`,
                        remove: true,
                    })),
                )
            })
            .catch((err) =>
                catchError(err.response.data, () => {
                    localStorage.removeItem('tokenAdmin')
                    user.adminData = undefined
                    window.location.reload()
                }),
            )
    }

    const loadStats = () => {
        apiClient
            .get('/badnet/stored', {
                params: {
                    ligueId: 3306,
                    lang: lng,
                },
            })
            .then(({ data }) => {
                const allTournaments = data.data
                const coming = allTournaments.filter((t: BadnetTournamentModel) => t.isComing)
                const past = allTournaments.filter((t: BadnetTournamentModel) => !t.isComing)
                setStats({
                    total: allTournaments.length,
                    coming: coming.length,
                    past: past.length,
                })
            })
            .catch((err) => console.error('Error loading stats:', err))
    }

    const syncBadnetTournaments = async () => {
        verifyToken()
        setSyncing(true)
        try {
            const response = await apiClient.post('/badnet/sync-manual')
            const result = response.data

            if (result.success && result.data) {
                sweetAlertSuccess(t('admin.badnet.sync-scheduled'))
                loadTournaments()
                loadStats()
            } else {
                sweetAlertSuccess(result.error || 'Synchronisation terminée')
            }
        } catch (error: any) {
            catchError(error.response?.data || 'Erreur lors de la synchronisation', () => {
                localStorage.removeItem('tokenAdmin')
                user.adminData = undefined
                window.location.reload()
            })
        } finally {
            setSyncing(false)
        }
    }

    const deleteOldTournaments = async () => {
        verifyToken()
        try {
            const response = await apiClient.delete('/badnet/old', {
                params: { days: 365 },
            })
            sweetAlertSuccess(`${response.data.data.count} tournois supprimés`)
            loadTournaments()
            loadStats()
        } catch (error: any) {
            catchError(error.response?.data || 'Erreur lors de la suppression', () => {
                localStorage.removeItem('tokenAdmin')
                user.adminData = undefined
                window.location.reload()
            })
        }
    }

    const deleteById = (id: number) => {
        verifyToken()
        apiClient
            .delete(`/badnet/${id}`)
            .then((res) => {
                sweetAlertSuccess('La suppression de ce tournoi est réussie').then(() => {
                    setTournaments(tournaments?.filter((t) => t.id !== id))
                    loadStats()
                })
            })
            .catch((err) =>
                catchError(err.response.data, () => {
                    localStorage.removeItem('tokenAdmin')
                    user.adminData = undefined
                    window.location.reload()
                }),
            )
    }

    if (!tournaments) return <Loader />

    return (
        <>
            <TitleWrapper>
                <Title dataTestId="admin-badnet-title">{t('admin.badnet.title')}</Title>
                <div className="d-flex gap-2 mb-2 flex-wrap">
                    <button
                        onClick={syncBadnetTournaments}
                        disabled={syncing}
                        className="btn btn-primary"
                        style={{
                            backgroundColor: 'var(--primary-color, #B40000)',
                            borderColor: 'var(--primary-color, #B40000)',
                        }}
                    >
                        {syncing ? t('admin.badnet.syncing') : t('admin.badnet.sync-button')}
                    </button>
                    <button onClick={deleteOldTournaments} className="btn btn-danger">
                        {t('admin.badnet.delete-old')}
                    </button>
                    <button
                        onClick={() => setShowComing(true)}
                        className={`btn ${showComing ? 'btn-primary' : 'btn-secondary'}`}
                        style={
                            showComing
                                ? {
                                      backgroundColor: 'var(--primary-color, #B40000)',
                                      borderColor: 'var(--primary-color, #B40000)',
                                  }
                                : {}
                        }
                    >
                        {t('admin.badnet.upcoming')}
                    </button>
                    <button
                        onClick={() => setShowComing(false)}
                        className={`btn ${!showComing ? 'btn-primary' : 'btn-secondary'}`}
                        style={
                            !showComing
                                ? {
                                      backgroundColor: 'var(--primary-color, #B40000)',
                                      borderColor: 'var(--primary-color, #B40000)',
                                  }
                                : {}
                        }
                    >
                        {t('admin.badnet.past')}
                    </button>
                </div>
                {stats && (
                    <div className="d-flex gap-3 mb-3">
                        <div className="card p-3">
                            <strong>{t('admin.badnet.total')}:</strong> {stats.total}
                        </div>
                        <div className="card p-3">
                            <strong>{t('admin.badnet.upcoming')}:</strong> {stats.coming}
                        </div>
                        <div className="card p-3">
                            <strong>{t('admin.badnet.past')}:</strong> {stats.past}
                        </div>
                    </div>
                )}
            </TitleWrapper>
            <AdminContainer>
                <Table tableData={tournaments} tableHeader={tableHeader} deleteById={deleteById} />
            </AdminContainer>
        </>
    )
}

export default AdminBadnetTournaments
